import * as React from 'react'
import { styled } from '@mui/system'

const TimelineWrap = styled('ul')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  padding: '100px 0 0 60px',
  maxWidth: 1120,
  [theme.breakpoints.down('md')]: {
    padding: '50px 0 0 0'
  }
}))

const LineItem = styled('li')(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  listStyle: 'none',
  minHeight: 70,
  marginBottom: 20
}))

const Seperetor = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: 0,
  alignItems: 'center',
  flexDirection: 'column',
  marginRight: 50,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}))

const TimelineContent = styled('div')(({ theme }) => ({
  padding: '6px 70px 60px 16px',
  [theme.breakpoints.down('md')]: {
    padding: '0 0 40px 10px'
  }
}))

const SeperetorDot = styled('span')(({ theme }) => ({
  display: 'flex',
  fontSize: '26px',
  fontWeight: 800,
  marginBottom: 20,
  width: 46,
  height: 46,
  lineHeight: '46px',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  background: theme.palette.primary.main,
  color: theme.palette.wrapper.main
}))

const SeperetorLine = styled('span')(({ theme }) => ({
  flexGrow: 1,
  border: '1px dashed #333370',
  width: 1,
  alignItems: 'center'
}))

const LineTitle = styled('h3')(({ theme }) => ({
  color: '#fff',
  fontSize: '2.5rem',
  lineHeight: '0.75',
  margin: '0 0 30px 0',
  fontWeight: 900,
  '&:target:before': {
    content: '""',
    display: 'block',
    height: '100px',
    margin: '-100px 0 0',
  }
}))

const LineTitleTM = styled('span')(({ theme }) => ({
  color: '#272e42',
  fontSize: '2.5rem'
}))

const LineText = styled('p')(({ theme }) => ({
  fontSize: '1.125rem',
  lineHeight: '1.66667',
  margin: '0 0 1rem 0',
  color: theme.palette.wrapper.contrastText,
  fontWeight: 700
}))

const TextItalic = styled('span')(({ theme }) => ({
  fontStyle: 'italic'
}))

export default function AboutUsContent () {
  return (
    <TimelineWrap>
      <LineItem>
        <Seperetor>
          <SeperetorDot>1</SeperetorDot>
          <SeperetorLine />
        </Seperetor>
        <TimelineContent>
          <LineTitle>Mission</LineTitle>
          <LineText>We are on a mission to democratize access to health, starting with its primary lever: What we do with our bodies.</LineText>
        </TimelineContent>
      </LineItem>
      <LineItem>
        <Seperetor>
          <SeperetorDot>2</SeperetorDot>
          <SeperetorLine />
        </Seperetor>
        <TimelineContent>
          <LineTitle>Movement</LineTitle>
          <LineText><TextItalic>We want to move you.</TextItalic></LineText>
          <LineText><TextItalic>We want to move you</TextItalic> -- physically, emotionally, and spiritually -- by providing the tools you need to succeed in your health and fitness journey.</LineText>
          <LineText><TextItalic>We want to move you</TextItalic> -- away from injury or pain, and towards health and better human performance -- regardless of your innate capabilities or starting point.</LineText>
          <LineText><TextItalic>We want to move you</TextItalic> -- with unparalleled technology, expertise, and precision -- no matter where you are or what equipment you have available.</LineText>
          <LineText><TextItalic>We want to move you</TextItalic> -- because we are starting a movement -- to reclaim our bodies, our health, and our futures.</LineText>
          <LineText><TextItalic>We want to move you</TextItalic> -- because it starts with you.</LineText>
        </TimelineContent>
      </LineItem>
      <LineItem>
        <Seperetor>
          <SeperetorDot>3</SeperetorDot>
          <SeperetorLine />
        </Seperetor>
        <TimelineContent>
          <LineTitle>Altis <LineTitleTM>™</LineTitleTM></LineTitle>
          <LineText>We created Altis because of a massive disconnect in connected fitness. Connected fitness is connected to the internet but not to the most critical piece of the equation: you. Despite the claims of some products, none of them see you, understand you, and personally instruct you the way a qualified personal trainer does. This leads to pain. Physically, when we get injured performing a cookie cutter program that isn&apos;t designed for our bodies. Physically, too, when we perform movements incorrectly – without being made aware we are even doing so. But perhaps even worse, when we are pained emotionally, by not getting results or feeling like we want to give up.</LineText>
          <LineText>We wanted to fix this.</LineText>
          <LineText>So we started with the hardest part first, computer vision. Computer vision is the ability of a computer to see. And while some software and hardware products made this possible, none of the available options saw the human body accurately enough to detect incorrect form and provide instantaneous form feedback to users.</LineText>
        </TimelineContent>
      </LineItem>
      <LineItem>
        <Seperetor>
          <SeperetorDot>4</SeperetorDot>
          <SeperetorLine />
        </Seperetor>
        <TimelineContent>
          <LineTitle>Body GPS <LineTitleTM>™</LineTitleTM></LineTitle>
          <LineText>We created Body GPS to solve this problem. Altis&apos;s computer vision neural network, a type of artificial intelligence (AI), is like GPS for the human body. Altis is able to determine where every part of your body is in 3D, in real time, without the use of sensors or any special equipment.</LineText>
        </TimelineContent>
      </LineItem>
      <LineItem>
        <Seperetor>
          <SeperetorDot>5</SeperetorDot>
          <SeperetorLine />
        </Seperetor>
        <TimelineContent>
          <LineTitle>Altis Vision <LineTitleTM>™</LineTitleTM></LineTitle>
          <LineText>But as remarkable as it is, software knowing where your body is only half the battle. Your ability to understand what your body is doing is equally important. Altis Vision is a proprietary feature of our user interface that processes the data from Body GPS to provide you with a live view of your body while exercising, from the most advantageous angle, along with easy to follow on-screen graphics, to improve and correct your form.</LineText>
        </TimelineContent>
      </LineItem>
      <LineItem>
        <Seperetor>
          <SeperetorDot>6</SeperetorDot>
        </Seperetor>
        <TimelineContent>
          <LineTitle id="cerebrus">Cerebrus <LineTitleTM>™</LineTitleTM></LineTitle>
          <LineText>Even with Body GPS and Altis Vision, the most accurate and advanced technologies for body awareness and movement instruction, the glaring missing piece is personalized program design. Our bodies come in different shapes and sizes, and our joints and muscles all express movement differently, from strength, to technique, to range of motion.</LineText>
          <LineText>Even though some digital fitness companies claim to address these differences and provide personalized programs, they do not. They, at best, provide canned, generic programs and video content users can choose from a menu. A pro-athlete that must rely on his or her body, doesn&apos;t train this way. So why would you?</LineText>
          <LineText>Your body is your most valuable asset, which is exactly how Altis treats it. Using a proprietary machine learning model, a type of artificial intelligence (AI), we&apos;ve named Cerebrus. Altis first learns about your body as you perform assessments that determine the capabilities and limitations of your joints - which help Altis understand how your body moves.</LineText>
          <LineText>Based on your body&apos;s assessment data, and of course your personal goals and schedule, Altis creates the optimal exercise regimen for your body. Even better, Altis will help improve areas requiring attention flagged during the assessment -- and continuously fine-tunes your program in real time based on your performance, from proper weight loads to exercise selection, so that you get the absolute most out of every single rep, every set, every exercise, every day. For life.</LineText>
          <LineText>Cerebrus was trained on the intelligence of the world&apos;s top Doctors of Physical Therapy and Strength and Conditioning Coaches — experts whose accolades include directing pro and Olympic teams and client rosters that count Lebron James, Scarlett Johansson, and Ryan Reynolds.</LineText>
        </TimelineContent>
      </LineItem>
    </TimelineWrap>
  )
}
